import { error } from "@sveltejs/kit";
import { normalizeForComparison, toNumeric } from "$lib/helpers/functions";
import {
  getCurrency,
  getNonEmptyRows,
  isISODate,
  isValidDate,
  isValidTime,
} from "$lib/helpers/validate";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
dayjs.extend(localizedFormat);
const localeModules = import.meta.glob(`/node_modules/dayjs/esm/locale/*.js`);

export const mathFunctions = [
  {
    value: "max",
    info: `Returns the larger of two numbers.
    max(1, 2)
    max(FieldA, FieldB)
    `,
    applyText: "max()",
    applyCursor: -1,
  },
  {
    value: "min",
    info: `Returns the smaller of two numbers.
    min(1, 2)
    min(FieldA, FieldB)`,
    applyText: "min()",
    applyCursor: -1,
  },
  {
    value: "format",
    info: `Formats a number using the locale.
    format(1234.56)
    format(FieldA)`,
    applyText: "format()",
    applyCursor: -1,
  },
  {
    value: "abs",
    info: `Returns the absolute value of a number.
    abs(-1)
    abs(FieldA)
    `,
    applyText: "abs()",
    applyCursor: -1,
  },
  {
    value: "sqrt",
    info: `Returns the square root of a number.
    sqrt(4)
    sqrt(FieldA)
    `,
    applyText: "sqrt()",
    applyCursor: -1,
  },
  {
    value: "if",
    info: `Returns one value if a condition is true and another if it is false.
    if(CheckField, 100, 0)
    if(CheckField, FieldOne, FieldTwo)`,
    applyText: "if(, true, false)",
    applyCursor: -14,
  },
  {
    value: "currency",
    info: `Formats a number as a currency value.
    currency(1234.56, USD)
    currency(FieldA, EUR)`,
    applyText: "currency(, USD)",
    applyCursor: -6,
  },
  {
    value: "round",
    info: `Rounds a number to a specified number of decimal places.
    round(1234.5678, 2)
    round(FieldA, 2)
      `,
    applyText: "round(, 2)",
    applyCursor: -4,
  },
  {
    value: "left",
    info: `Returns the leftmost characters of a string.
    left(Hello, 2)
    left(FieldA, 2)
      `,
    applyText: "left(, 4)",
    applyCursor: -4,
  },
  {
    value: "right",
    info: `Returns the rightmost characters of a string.
    right(Hello, 2)
    right(FieldA, 2)
      `,
    applyText: "right(, 4)",
    applyCursor: -4,
  },
  {
    value: "mid",
    info: `Returns a substring from a string.
    mid(Hello, 2, 4)
    mid(FieldA, 2, 4)
    `,
    applyText: "mid(, 2, 4)",
    applyCursor: -7,
  },
  {
    value: "split",
    info: `Splits a string and returns the left (0) or right (1) part.
    split(Website, '.', 0)
    split(Website, '.', 1)
    `,
    applyText: "split(, '.', 1)",
    applyCursor: -9,
  },
  {
    value: "upper",
    info: "Converts a string to uppercase",
  },
  {
    value: "lower",
    info: "Converts a string to lowercase",
  },
  {
    value: "day",
    info: "Returns the day of the month",
    applyText: "day()",
    applyCursor: -1,
  },
  {
    value: "date",
    info: "Returns the date in specified format",
    applyText: "date(, 'DD/MM/YYYY')",
    applyCursor: -15,
  },
  {
    value: "month",
    info: "Returns the month of the year",
    applyText: "month()",
    applyCursor: -1,
  },
  {
    value: "year",
    info: "Returns the year",
    applyText: "year()",
    applyCursor: -1,
  },
];

const other_variables = [
  "response_summary",
  "response_id",
  "time_spent",
  "form_id",
  "form_name",
  "form_language",
  "page_title",
  "ip_address",
  "country",
  "region",
  "city",
];

function isFunction(token: string | number): boolean {
  return mathFunctions.map((x) => x.value).includes(
    token.toString().toLowerCase(),
  );
}

function findVariable(object: any, key: string | number) {
  if (object[key]) return object[key];
  const lowerCaseKey = String(key).toLowerCase();
  const keyWithoutExtension = lowerCaseKey.replace(/\..+$/, "");
  const keyExtension = lowerCaseKey.replace(/^field\d+\./, "");
  for (const prop in object) {
    if (prop.toLowerCase() === keyWithoutExtension) {
      if (typeof object[prop] === "object" && object[prop] === null) {
        return "";
      }
      if (
        typeof object[prop] === "object" && keyExtension &&
        keyExtension in object[prop]
      ) {
        return object[prop][keyExtension];
      }
      return object[prop];
    }
  }
  return undefined;
}

export function listToArray(input: string, locale: string): string[] {
  // Create a ListFormat instance for the provided locale
  const formatter = new Intl.ListFormat(locale, {
    style: "long",
    type: "conjunction",
  });

  // Use it to create a localized "and" separator
  const andSeparator = formatter.format(["", ""]).trim();

  // Create a RegExp that matches any number of space characters followed by the separator followed by any number of space characters
  const separatorRegex = new RegExp(`,|\\s*${andSeparator}\\s*`, "g");

  // Split the input string into parts by comma or the localized "and", then trim any leading/trailing white space from each part
  const result = input.split(separatorRegex).map((part) => part.trim()).filter(
    Boolean,
  ).filter((item) => item.trim() !== "," && item.trim() !== andSeparator);

  return result;
}

function findVariableInValues(
  variable: string,
  values: FormValues,
): string[] | string {
  const isLabels = /^field\d+\.labels$/i.test(variable);
  const isExtension = /^field\d+\..+$/i.test(variable);
  const search_variable = isExtension
    ? variable.replace(/\..+$/, "")
    : variable;
  const extension = isExtension ? variable.replace(/^field\d+\./, "") : "";
  const found = typeof findVariable(values, search_variable) !== "undefined"
    ? findVariable(values, search_variable)
    : "";
  if (found && Array.isArray(found)) {
    return found.map((f) =>
      "key" in f ? isLabels ? f.name : f.key : isLabels ? f.label : f.value
    );
  }
  if (found && typeof found === "object" && "start_time" in found) {
    return found.start_time;
  }
  if (found && typeof found === "object" && "value" in found) {
    return found.value;
  }
  if (found && typeof found === "object" && extension && extension in found) {
    return found[extension];
  }
  if (
    found && typeof found === "object" && "headers" in found && "rows" in found
  ) {
    const rows = getNonEmptyRows((found as TableEntryValue).rows);
    return rows.map((row) => {
      return row.filter(Boolean).join(", ");
    });
  }
  if (found && typeof found === "object" && "full_address" in found) {
    return found.full_address;
  }
  if (typeof found === "object") {
    return "";
  }
  if (found.includes(variable)) {
    return "";
  }
  return found;
}

function containsFormula(str: string) {
  var reg = /{.*}/;
  return reg.test(str);
}

type Numeric = number | string;
type NumericObject = { label: string; value: Numeric };

export function getNumericValue(
  input: Numeric | Numeric[] | NumericObject | NumericObject[] | object,
): number | false {
  if (typeof input === "number") {
    return input;
  } else if (typeof input === "string") {
    return !isNaN(parseFloat(input)) && isFinite(Number(input))
      ? Number(input)
      : false;
  } else if (Array.isArray(input)) {
    let numbers = input.map(getNumericValue).filter((item) =>
      item !== false
    ) as number[];
    return numbers.reduce((prev, curr) => prev + curr, 0);
  } else if (typeof input === "object") {
    return "label" in input && "value" in input &&
        getNumericValue(input.value) !== false
      ? getNumericValue(input.value)
      : false;
  }
  return false;
}

export function isNumericArray(arr: (number | string)[]): boolean {
  for (const item of arr) {
    if (!isNumeric(item)) {
      return false;
    }
  }
  return true;
}

export function isNumeric(num: number | string | null | undefined): boolean {
  if (num === undefined || num === null) {
    return false;
  }
  return !isNaN(parseFloat(num as string)) && isFinite(num as number);
}

/**
 * Returns an array of tokens from the input expression string.
 */
function tokenize(input: string): (string | number)[] {
  // First, we'll initialize a `scanner` index to track how much
  // of the input string we've covered
  let scanner = 0;

  // We'll also need an array to put the tokens we find
  const tokens: (string | number)[] = [];

  // While we haven't reached the end of the input...
  while (scanner < input.length) {
    // Get the next character
    const char = input[scanner];

    // If the character is a single quote...
    if (char === "'") {
      // Create a string to hold all the characters in the string
      let string = "";

      // Move the scanner to the next character
      scanner++;

      // While we have more characters in the string and we haven't
      // gotten to the end of the input...
      while (scanner < input.length && input[scanner] !== "'") {
        // Collect all the characters in the string
        string += input[scanner++];
      }

      // Move the scanner to the next character
      scanner++;

      // Push the full string to the array of tokens
      tokens.push(`'${string}'`);
      continue;
    }

    // If the character is a number...
    if (/[0-9]/.test(char)) {
      // Create a string to hold all the digits in the number
      let digits = "";

      // While we have more digits (or a period) in the number and we haven't
      // gotten to the end of the input...
      while (scanner < input.length && /[0-9\.]/.test(input[scanner])) {
        // Collect all the digits
        digits += input[scanner++];
      }

      // Convert the digits to a number and push the number to the array of tokens
      const number = parseFloat(digits);
      tokens.push(number);
      continue;
    }

    // If the character is a symbol...
    if (/[+\-/*%(),^<>=]/.test(char)) {
      // Push it to the array of tokens
      tokens.push(char);
      scanner++;
      continue;
    }

    // If the character is white space...
    if (char === " ") {
      // Ignore it
      scanner++;
      continue;
    }

    // If the character is the first character of a name,
    // like the name of a function or variable or variable pointer
    if (/[a-zA-Z]/.test(char)) {
      // Create a string to hold all the characters in the name
      let name = "";

      // While we have more characters that make up the name and we haven't
      // gotten to the end of the input...
      while (scanner < input.length && /[a-zA-Z0-9_.]/.test(input[scanner])) {
        // Collect all the characters in the name
        name += input[scanner++];
      }

      // Then push the full name to the array of tokens
      tokens.push(name);
      continue;
    }

    // If we can't recognize the character, we'll throw an error
    // continue
    error(400, {
      message:
        `Math: Invalid character "${char}" at position "${scanner}" in the formula "${input}"`,
    });
  }

  // After collecting all the tokens in the expression, we'll return them
  return tokens;
}

/**
 * Converts the tokens in infix notation to Reverse Polish notation
 */
function toRPN(tokens: (string | number)[]): (string | number)[] {
  // First, we'll set up a stack to hold operators we're not yet ready to
  // add to the final expression
  const operators: string[] = [];

  // ...and an array to hold the final RPN expression
  const out: (string | number)[] = [];

  // For each token in the infix expression...
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    // If the token is a number...
    if (typeof token === "number") {
      // We'll push it to `out`
      out.push(token);
      continue;
    }

    if (typeof token === "string" && token === "") {
      out.push(token);
      continue;
    }

    // If token is string in the format 'string'
    if (
      typeof token === "string" && token.startsWith("'") && token.endsWith("'")
    ) {
      out.push(token);
      continue;
    }

    // If the token is an operator or a function name...
    if (/[+\-/*%<>=^a-zA-Z_.]/.test(token)) {
      // While there are operators in the `operators` stack with a higher
      // precedence than the current token, we'll unwind them `operators` on to `out`
      while (shouldUnwindOperatorStack(operators, token)) {
        out.push(operators.pop()!);
      }

      // And then, push the current token to the `operators` stack
      operators.push(token);
      continue;
    }

    // If the token is a left parenthesis symbol...
    if (token === "(") {
      // We'll push it to the `operators` stack
      operators.push(token);
      continue;
    }

    // If the token is a right parenthesis symbol...
    if (token === ")") {
      // While there are operators in the `operators` stack, and we haven't reached the last left parenthesis symbol, we'll unwind them on to `out`
      while (operators.length > 0 && operators[operators.length - 1] !== "(") {
        out.push(operators.pop()!);
      }

      // We no longer need the left parenthesis symbol, so we'll discard it
      operators.pop();
      continue;
    }

    // If the token is a comma...
    if (token === ",") {
      // While there are operators in the `operators` stack, and we haven't reached the
      // last left parenthesis symbol, we'll unwind them on to `out`
      while (operators.length > 0 && operators[operators.length - 1] !== "(") {
        out.push(operators.pop()!);
      }
      continue;
    }

    // If we can't recognize the token, we'll throw an error
    error(400, { message: `Math: Invalid token "${token}"` });
  }

  // Finally we'll unwind all the remaining operators on to `out`
  for (let i = operators.length - 1; i >= 0; i--) {
    out.push(operators[i]);
  }

  // And then return `out`
  return out;
}

// BODMAS, PEMDAS
// Exponentiation > [multiplication, division] > [addition, subtraction]
const precedence: { [key: string]: number } = {
  "^": 3,
  "*": 2,
  "/": 2,
  "+": 1,
  "-": 1,
};

// Returns true if the topmost operator in the `operators` stack has a
// precedence higher than or equal to the precedence of `nextToken`
function shouldUnwindOperatorStack(
  operators: string[],
  nextToken: string | number,
) {
  if (operators.length === 0) {
    return false;
  }

  const lastOperator = operators[operators.length - 1];
  return /[a-zA-Z]/.test(lastOperator) ||
    (precedence[lastOperator] &&
      precedence[lastOperator] >= precedence[nextToken]);
}

/**
 * Evaluates the RPN expression
 */
const evalRPN = async (
  rpn: (string | number)[],
  variables: FormValues,
  input: string,
  locale: string,
) => {
  const stack: (string | number)[] = [];

  // For each token in the RPN expression...
  for (let i = 0; i < rpn.length; i++) {
    const token = rpn[i];

    // If token is a string in the format 'string'
    if (
      typeof token === "string" && token.startsWith("'") && token.endsWith("'")
    ) {
      stack.push(token);
      continue;
    }

    // If the token is an operator...
    if (/[+\-/%*^<>=]/.test(token.toString())) {
      // Operate on the stack and push the result back on to the stack
      stack.push(operate(token.toString(), stack));
      continue;
    }

    // If the token is a variable...
    if (
      /^field/i.test(token.toString()) ||
      /^action/i.test(token.toString()) ||
      /^lookup/i.test(token.toString()) ||
      other_variables.includes(token.toString())
    ) {
      let value;

      if (typeof findVariable(variables, token) === "undefined") {
        value = "";
      } else {
        const shorthand = findVariable(variables, token);
        const shorthand2 = findVariableInValues(token.toString(), variables);
        if (Array.isArray(shorthand) && Array.isArray(shorthand2)) {
          if (isNumericArray(shorthand2)) {
            value = (shorthand as ChoiceValue[]).reduce(
              (acc: number, curr: ChoiceOption) => {
                if (isNumeric(curr.value)) {
                  return acc + parseFloat(curr.value.toString());
                }
                return acc;
              },
              0,
            );
          } else {
            const formatter = new Intl.ListFormat(locale);
            value = formatter.format(shorthand2);
          }
        } else if (
          containsFormula(shorthand.toString())
        ) {
          // If the variable is a formula, we'll evaluate it
          try {
            value = await evaluate(shorthand.toString(), variables, locale);
          } catch (err) {
            error(400, {
              message:
                `Math: The formula "{${input}}" contains a reference to "${shorthand}" which cannot be evaluated as a number`,
            });
          }
        } else if (typeof shorthand !== "object") {
          if (isValidDate(shorthand) || isValidTime(shorthand)) {
            value = normalizeForComparison(shorthand);
          } else {
            value = isNumeric(shorthand)
              ? parseFloat(shorthand.toString())
              : shorthand.toString();
          }
        } else if (typeof shorthand.value !== "undefined") {
          value = parseFloat(shorthand.value.toString());
        } else {
          value = "";
        }
      }

      // But if it has, we'll push the value to the stack
      if (value) {
        stack.push(
          isNumeric(value) ? parseFloat(value.toString()) : value.toString(),
        );
      } else stack.push("");
      continue;
    }

    // If the token is a function name...
    if (isFunction(token)) {
      // Apply the function on the stack and push the result to the stack
      stack.push(apply(token.toString(), stack, input, locale));
      continue;
    }

    if (/^[a-zA-Z]+$/.test(token.toString())) {
      stack.push(token.toString());
      continue;
    }

    // If the token is a number or a variable pointer, push it to the stack
    if (typeof token === "number") {
      stack.push(parseFloat(token.toString()));
      continue;
    }

    stack.push(token.toString());
    continue;

    // If we can't recognize the token, we'll throw an error
    error(400, { message: `Math: Invalid token ${token} (2)` });
  }

  // The value left on the stack is the final result of the evaluation
  return stack.pop();
};

function getStringValue(value: string | number | boolean): string {
  if (typeof value === "string") return value.replace(/^'|'$/g, "");
  if (typeof value === "number") return value.toString();
  if (typeof value === "boolean") return value ? "true" : "false";
  return "";
}

/**
 * Returns the result of appyling the mathematical operator on the stack.
 * The operator here is either an arithmetic or a logical operator, so we
 * only need two operands from the stack for any operator.
 */
function operate(operator: string, stack: (string | number)[]) {
  let b = stack.pop();
  let a = stack.pop();
  b = typeof b === "undefined" ? 0 : b;
  a = typeof a === "undefined" ? 0 : a;
  const a_string = getStringValue(a);
  const b_string = getStringValue(b);
  a = typeof a === "string" ? parseFloat(a.replace(/^'|'$/g, "")) : a;
  b = typeof b === "string" ? parseFloat(b.replace(/^'|'$/g, "")) : b;
  a = isNaN(a) ? 0 : a;
  b = isNaN(b) ? 0 : b;

  switch (operator) {
    case "+": {
      return a + b;
    }
    case "-": {
      return a - b;
    }
    case "*": {
      return a * b;
    }
    case "/": {
      return a / b;
    }
    case "^": {
      return Math.pow(a, b);
    }
    case "<": {
      return a < b ? 1 : 0;
    }
    case ">": {
      return a > b ? 1 : 0;
    }
    case "=": {
      if (!isNumeric(b_string) || !isNumeric(a_string)) {
        return a_string.trim() === b_string.trim() ? 1 : 0;
      } else {
        return a === b ? 1 : 0;
      }
    }
    case "%": {
      return a % b;
    }
    default:
      error(400, { message: `Invalid operator: ${operator}` });
  }
}

/**
 * Returns the result of applying the function onto the stack.
 *
 * Functions may have any number of arguments. But each function must have a
 * definite number of arguments. i.e. X(a, b) and Y(a, b, c) are possible, but
 * X(a, b) and X(a, b, c) are not possible.
 *
 * The function arguments are in right-to-left order in the stack, i.e. the rightmost
 * argument is at the top of the stack, and so on.
 */
function apply(
  func: string,
  stack: (string | number)[],
  input: string,
  locale: string = "en",
): string | number {
  let localeToUse: string | string[] = [locale];

  try {
    localeToUse = Intl.NumberFormat.supportedLocalesOf(locale);
  } catch (e) {
    localeToUse = ["en"];
  }

  // MAX(a, b) returns the larger of a and b
  if (func.toLowerCase() === "max") {
    const b = toNumeric(stack.pop());
    const a = toNumeric(stack.pop());
    return Math.max(a, b);
  }

  // MIN(a, b) returns the larger of a and b
  if (func.toLowerCase() === "min") {
    const b = toNumeric(stack.pop());
    const a = toNumeric(stack.pop());
    return Math.min(a, b);
  }

  localeToUse = localeToUse[0];

  // FORMAT(a) returns the format value of a
  if (func.toLowerCase() === "format") {
    const a = toNumeric(stack.pop());
    const parts = new Intl.NumberFormat(localeToUse).formatToParts(a);
    const fractionDigits = parts.filter((part) =>
      part.type === "fraction"
    ).toString().length;
    return new Intl.NumberFormat(localeToUse, {
      maximumFractionDigits: fractionDigits,
    }).format(a);
  }

  // ABS(a) returns the absolute value of a
  if (func.toLowerCase() === "abs") {
    const a = toNumeric(stack.pop());
    return Math.abs(a);
  }

  // CURRENCY(a, b) returns the currency value of a in currency b
  if (func.toLowerCase() === "currency") {
    const a = stack.pop();
    const b = toNumeric(stack.pop());
    const parts = new Intl.NumberFormat(localeToUse).formatToParts(b);
    const fractionDigits = parts.filter((part) =>
      part.type === "fraction"
    ).toString().length;
    const currency = getCurrency(typeof a === "number" ? "" : a);
    return new Intl.NumberFormat(localeToUse, {
      style: "currency",
      currency: currency,
      maximumFractionDigits: fractionDigits,
    }).format(b);
  }

  // LEFT(a, b) returns the leftmost b characters of a
  if (func.toLowerCase() === "left") {
    const b = toNumeric(stack.pop());
    const a = stack.pop();
    return (a ?? "").toString().substring(0, b);
  }

  // RIGHT(a, b) returns the rightmost b characters of a
  if (func.toLowerCase() === "right") {
    const b = toNumeric(stack.pop());
    const a = stack.pop();
    return (a ?? "").toString().substring((a ?? "").toString().length - b);
  }

  // DAY(a) returns the day of the month of a (where as is the difference between the date and today)
  if (func.toLowerCase() === "day") {
    const a = toNumeric(stack.pop());
    const new_date = dayjs().add(a, "day");
    return new_date.format("D");
  }

  // DATE(a, b) returns the date, formatted in the b format
  if (func.toLowerCase() === "date") {
    const b = stack.pop();
    const a = toNumeric(stack.pop());
    const new_date = dayjs().add(a, "day");
    return new_date.format(getStringValue(b));
  }

  // MONTH(a) returns the month of the year of a (where as is the difference between the date and today)
  if (func.toLowerCase() === "month") {
    const a = toNumeric(stack.pop());
    const new_date = dayjs().add(a, "day");
    return new_date.format("M");
  }

  // YEAR(a) returns the year of a (where as is the difference between the date and today)
  if (func.toLowerCase() === "year") {
    const a = toNumeric(stack.pop());
    const new_date = dayjs().add(a, "day");
    return new_date.format("YYYY");
  }

  // MID(a, b, c) returns the c characters of a starting from b
  if (func.toLowerCase() === "mid") {
    const c = toNumeric(stack.pop());
    const b = toNumeric(stack.pop());
    const a = stack.pop();
    return (a ?? "").toString().substring(b, b + c);
  }

  // SPLIT(a, b, c) returns the left or right part of a split by b
  if (func.toLowerCase() === "split") {
    const c = toNumeric(stack.pop());
    const b = stack.pop();
    const a = stack.pop();
    const parts = (a ?? "").toString().split(getStringValue(b), 2);
    return c === 0 ? parts[0] : parts[1];
  }

  // UPPER(a) returns the uppercase value of a
  if (func.toLowerCase() === "upper") {
    const a = stack.pop();
    return (a ?? "").toString().toUpperCase();
  }

  // LOWER(a) returns the lowercase value of a
  if (func.toLowerCase() === "lower") {
    const a = stack.pop();
    return (a ?? "").toString().toLowerCase();
  }

  // ROUND(a, b) returns the rounded value of a to b decimal places
  if (func.toLowerCase() === "round") {
    const last = ["DOWN", "UP"].includes(
      stack[stack.length - 1].toString().toUpperCase(),
    );
    if (last) {
      const c = stack.pop();
      const b = toNumeric(stack.pop());
      const a = toNumeric(stack.pop());
      if (c === "DOWN") {
        return Math.floor(a * Math.pow(10, b)) / Math.pow(10, b);
      } else {
        return Math.ceil(a * Math.pow(10, b)) / Math.pow(10, b);
      }
    } else {
      const b = toNumeric(stack.pop());
      const a = toNumeric(stack.pop());
      const result = Math.round(a * Math.pow(10, b)) / Math.pow(10, b);
      return Math.round(a * Math.pow(10, b)) / Math.pow(10, b);
    }
  }

  // SQRT(a) returns the square-root of a
  if (func.toLowerCase() === "sqrt") {
    const a = toNumeric(stack.pop());
    return Math.sqrt(a);
  }

  // IF(a, b, c) returns b if a is true. Else, it returns c
  if (func.toLowerCase() === "if") {
    const ifFalse = stack.pop() || "";
    const ifTrue = stack.pop() || "";
    const checker = stack.pop();
    let predicate = Boolean(checker);
    if (
      typeof checker === "string" &&
      ["0", "false", "null", "no"].includes(checker.toLowerCase())
    ) {
      predicate = false;
    }
    return predicate ? getStringValue(ifTrue) : getStringValue(ifFalse);
  }

  // If we can't recognize the function, we'll throw an error
  error(400, {
    message: `Math: Undefined function: ${func} in the formula {${input}}`,
  });
}

/**
 * Finally, in the `evaluator` function, we'll link all stages
 * of the evaluation together.
 *
 * tokenize -> toRPN -> evalRPN
 */
export const evaluate = async (
  input: string = "{}",
  variables: FormValues = {},
  locale: string,
  display: boolean = true,
): Promise<string> => {
  if (typeof input === "string") {
    input = input.replace(/&gt;/g, ">").replace(/&lt;/g, "<");
  }
  input = /^field\d+$/i.test(input) ? `{${input}}` : input;

  // Look for global references
  if (
    /^[a-zA-Z0-9_.]+$/.test(input) &&
    typeof findVariable(variables, input) !== "undefined"
  ) {
    const foundVariable = findVariable(variables, input);
    if (!foundVariable) return "";
    if (typeof foundVariable !== "string") return "";
    return foundVariable;
  }

  const formulasInString = getFormulas(input);

  if (formulasInString.length === 0) return input;

  let result = input;

  for (let formula of formulasInString) {
    const evaluated = await evaluateOne(formula, variables, locale, display);
    result = result.replaceAll(formula, evaluated);
  }
  return result;
};

/**
 * Evaluates a formula as an array. Force using field1.value.
 *
 * @function
 * @name evaluateAsArray
 * @async
 * @param {string} [input="{}"] - The string to be evaluated.
 * @param {FormValues} [variables={}] - The form values used for evaluation.
 * @returns {Promise<string[]>} - The evaluated string as an array.
 */
export const evaluateAsArray = async (
  input: string = "{}",
  variables: FormValues = {},
): Promise<string[]> => {
  const original = [input];
  input = input.replace(/\s/gu, "");
  input = /^field\d+$/i.test(input) ? `{${input}}` : input;
  const exec = /^{(.*?)}$/.exec(input);
  if (exec === null || exec.length === 0) {
    return original;
  }
  let result = findVariableInValues(exec[1], variables);
  if (result === "") return [];
  return typeof result === "string" ? [result] : result;
};

/**
 * Evaluates an input string as a numeric value.
 *
 * @function
 * @name evaluateNumeric
 * @async
 * @param {string} [input="{}"] - The string to be evaluated.
 * @param {FormValues} [variables={}] - The form values used for evaluation.
 * @param {string} locale - The locale of the user.
 * @returns {Promise<string>} - The evaluated string as a numeric value.
 */
export const evaluateNumeric = async (
  input: string = "{}",
  variables: FormValues = {},
  locale: string,
): Promise<string> => {
  input = /^field\d+$/i.test(input) ? `{${input}}` : input;
  let inputTrimmed = input.replace(/{([^}]+)}/g, "{($1)}");
  return await evaluate(inputTrimmed, variables, locale);
};

// Used for {field1} and {field1.value} not {field1+field2}
const evaluateOneVariable = async (
  input: string,
  variables: FormValues = {},
  locale: string,
  display: boolean = true,
): Promise<string> => {
  const shorthand = findVariableInValues(input, variables);
  // const fieldType = findFieldType(input, variables);
  const is_table_entry = typeof variables[input] === "object" &&
    "headers" in variables[input] && "rows" in variables[input];

  let value = shorthand;
  if (!Array.isArray(shorthand) && containsFormula(shorthand.toString())) {
    try {
      value = (await evaluate(shorthand.toString(), variables, locale)).trim();
    } catch {
      error(400, {
        message:
          `Math: The formula "{${input}}" contains a reference to "${shorthand}" which cannot be evaluated as a number (evaluateOneVariable)`,
      });
    }
  }

  // String vs array turned into comma separated string
  let result;
  if (Array.isArray(value)) {
    if (value.length === 0) {
      result = "";
    } else if (is_table_entry) {
      result = value.join("\r\n");
    } else if (isNumericArray(value)) {
      const sum = value.reduce((a, b) => a + parseFloat(b), 0);
      result = sum.toString();
    } else {
      const formatter = new Intl.ListFormat(locale);
      result = display ? formatter.format(value) : value.join(",");
    }
  } else if (isISODate(value)) {
    if (
      locale !== "en" &&
      typeof localeModules[`/node_modules/dayjs/esm/locale/${locale}.js`] !==
        "undefined"
    ) {
      const module: any = await localeModules
        [`/node_modules/dayjs/esm/locale/${locale}.js`]();
      dayjs.locale(locale, module.default);
    }
    result = display ? dayjs(value).locale(locale).format("LLL") : value;
  } else if (isValidDate(value)) {
    if (
      locale !== "en" &&
      typeof localeModules[`/node_modules/dayjs/esm/locale/${locale}.js`] !==
        "undefined"
    ) {
      const module: any = await localeModules
        [`/node_modules/dayjs/esm/locale/${locale}.js`]();
      dayjs.locale(locale, module.default);
    }
    result = display ? dayjs(value).locale(locale).format("LL") : value;
  } else {
    result = value;
  }
  return result;
};

const evaluateOne = async (
  input: string,
  variables: FormValues = {},
  locale: string,
  display: boolean = true,
): Promise<string> => {
  let inputTrimmed = input.trim();
  const exec = /^{(.*?)}$/.exec(inputTrimmed);
  if (exec === null || exec[1] === "") return "";

  // Match {field1}
  const allowedExtensions = [
    "label",
    "labels",
    "line1",
    "line2",
    "city",
    "state",
    "postal_code",
    "country",
  ];
  const allowedExtensionsPattern = allowedExtensions.join("|");
  if (
    /^field\d+$/i.test(exec[1]) ||
    new RegExp(`^field\\d+\\.(${allowedExtensionsPattern})$`, "i").test(exec[1])
  ) {
    // Returns any string
    inputTrimmed = exec === null ? inputTrimmed : exec[1];
    const evaluated = await evaluateOneVariable(
      inputTrimmed,
      variables,
      locale,
      display,
    );
    return typeof evaluated !== "undefined" ? evaluated.toString() : "";
  } else if (/^[a-zA-Z0-9_.]+$/.test(exec[1])) {
    // For other stuff like {response_summary} and {wfa5}
    const foundVariable = findVariable(variables, exec[1].trim());
    if (!foundVariable) return "";
    if (typeof foundVariable !== "string") return "";
    return foundVariable;
  } else {
    // Returns any string
    inputTrimmed = exec === null ? inputTrimmed : exec[1];
    inputTrimmed = inputTrimmed.replace(/(field\d+)\.value/gi, "$1");
    const tokens = tokenize(inputTrimmed);
    const RPN = toRPN(tokens);
    const evaluated = await evalRPN(RPN, variables, inputTrimmed, locale);
    return typeof evaluated !== "undefined" ? evaluated.toString() : "0";
  }
};

export function evaluateBoolean(value: any): boolean {
  // Normalize the value if it's a string
  if (typeof value === "string") {
    value = value.trim().toLowerCase();
  }

  // Explicitly check for normalized string values 'false' and 'no'
  if (value === "false" || value === "no") {
    return false;
  }

  // List of other falsish values
  const falsishValues = [false, 0, "", null, undefined, -1];

  // Check if the value is in the list of falsish values
  if (falsishValues.includes(value)) {
    return false;
  }

  // Return true for everything else
  return true;
}

export const getFormulas = (text: string): string[] => {
  let match;
  let formulas: string[] = [];
  const regex = /\{([^{}]*?)\}/g;

  while ((match = regex.exec(text)) !== null) {
    const formula = `{${match[1]}}`;

    // Check if the match is not part of double braces
    const preChar = text[match.index - 1];
    const postChar = text[regex.lastIndex];

    if (preChar !== "{" && postChar !== "}") {
      if (!formulas.includes(formula)) {
        formulas.push(formula);
      }
    }
  }

  return formulas.filter(Boolean);
};
